import { useState, type ReactNode } from "react";
import { OfficialBadge } from "./OfficialBadge";
import { avatarPhotoCopyFor, avatarPhotoUrl } from "./profile-ui";

/**
 * Preset avatars: a themed emoji on a per-identity gradient disc (matching the
 * Bing card-room design's avatar tokens). The ids map 1:1 to `AVATARS` in
 * `@bing/shared`.
 */
const AVATAR_EMOJI: Record<string, string> = {
  fox: "🦊",
  owl: "🦉",
  lion: "🦁",
  panda: "🐼",
  cat: "🐱",
  dog: "🐶",
  tiger: "🐯",
  bear: "🐻",
  rabbit: "🐰",
  wolf: "🐺",
  eagle: "🦅",
  frog: "🐸",
};

/** Gradient per identity — the design's avatar palette. */
const AVATAR_GRADIENT: Record<string, string> = {
  fox: "linear-gradient(140deg,#e0954a,#c46a1f)",
  owl: "linear-gradient(140deg,#8e9aa8,#5c6673)",
  lion: "linear-gradient(140deg,#d9b45c,#b8862f)",
  panda: "linear-gradient(140deg,#5b8def,#3a5fd0)",
  cat: "linear-gradient(140deg,#ec6ea5,#c23b78)",
  dog: "linear-gradient(140deg,#e0954a,#c46a1f)",
  tiger: "linear-gradient(140deg,#ff8a5b,#e5544b)",
  bear: "linear-gradient(140deg,#a6764a,#7a4f2a)",
  rabbit: "linear-gradient(140deg,#f2606a,#c9333e)",
  wolf: "linear-gradient(140deg,#7b61ff,#4a3bd6)",
  eagle: "linear-gradient(140deg,#2fb0c9,#1f7a8c)",
  frog: "linear-gradient(140deg,#57c8a8,#2f8f78)",
};

/**
 * The shared avatar disc. When `avatarPhotoId` is set the user has an uploaded
 * photo, rendered as a round `<img>` from `GET /avatar/:id`; otherwise it falls
 * back to the preset emoji/gradient identified by `avatarId`. Existing preset-id
 * callers keep working unchanged (they simply omit `avatarPhotoId`).
 *
 * THE PHOTO IS THE COPY SIZED FOR THIS SPOT. `size` is the CSS px it is drawn at, so it asks for
 * the smallest copy with a source pixel per device pixel at this screen's density, never below DPR 3
 * (`GET /avatar/:id/{96|144|192}`, see `avatarPhotoCopyFor`), and the full image where no copy is
 * big enough — the profile heroes at 80 and 104 px always. Every caller gets this by passing the
 * `size` it already passes; drawing a photo any other way would bypass it.
 */
export function Avatar({
  avatarId,
  avatarPhotoId,
  size = 40,
  ringClass,
  ringWidth = 2,
  official,
}: {
  avatarId: string;
  avatarPhotoId?: string | null;
  size?: number;
  /**
   * Tailwind classes for the ring's COLOUR/shadow only (e.g. `"border-secondary
   * shadow-sm"`). Setting it renders the avatar inside the DETERMINISTIC ring
   * frame below — the ONLY sanctioned way to draw a ring around an avatar. Never
   * wrap `<Avatar>` in an ad-hoc `inline-flex rounded-full border-*` span: an
   * inline-level wrapper sits on the text BASELINE, and Android's Chromium adds
   * the line-box strut/descender to it, so the ring box no longer coincides with
   * the avatar circle and the photo spills outside the frame (the recurring
   * Android-only "avatar overflows its ring" bug — iOS WebKit synthesizes the
   * baseline differently, which is why iOS always looked fine).
   */
  ringClass?: string;
  /** Ring thickness in px (border-width; geometry stays deterministic). */
  ringWidth?: number;
  /**
   * Stamp the verified badge on the disc (an official account).
   *
   * Rendered HERE rather than beside the avatar at each call site, for the same reason
   * {@link ringClass} exists: the badge needs a positioned wrapper, and an ad-hoc one at nine
   * call sites is nine chances to reintroduce the Android baseline bug. The wrapper is pinned to
   * EXACTLY the dimensions of what it wraps and the badge is absolutely positioned inside it, so
   * turning this on cannot move anything — which is the property that had to be provable.
   */
  official?: boolean;
}): ReactNode {
  // Hard-lock the box to exactly `size` in BOTH axes. Some Android WebViews ignore
  // width/height on a flex `.avatar` and let the box grow (an image to its
  // intrinsic pixels, an emoji to its font metrics) until it overflows the screen;
  // pinning min = max = size + flex-shrink:0 makes that impossible on either path.
  // `box-sizing:border-box` keeps the box exactly `size` even inside a ring wrapper
  // that adds padding/border, and `overflow:hidden` clips either path to the circle.
  const box = {
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
    maxWidth: size,
    maxHeight: size,
    flexShrink: 0,
    overflow: "hidden",
    boxSizing: "border-box",
  } as const;

  // A COPY THAT WILL NOT LOAD FALLS BACK TO THE FULL IMAGE, ONCE — an api rolled back to before
  // the copies existed 404s them, and a broken picture is the one outcome this must never cost.
  // Remembered by the copy's URL, so it resets by itself when this <img> is reused for another
  // photo or another size (an <img> keeps its element across a seat changing hands).
  const [failedCopy, setFailedCopy] = useState<string | null>(null);
  const fullSrc = avatarPhotoId ? avatarPhotoUrl(avatarPhotoId) : "";
  const density = typeof window === "undefined" ? undefined : window.devicePixelRatio;
  const copySrc = avatarPhotoId
    ? avatarPhotoUrl(avatarPhotoId, avatarPhotoCopyFor(size, density))
    : "";
  const src = failedCopy === copySrc ? fullSrc : copySrc;

  const core = avatarPhotoId ? (
    <img
      className="avatar"
      src={src}
      onError={() => setFailedCopy(copySrc)}
      alt={avatarId}
      width={size}
      height={size}
      style={{ ...box, display: "block", objectFit: "cover" }}
    />
  ) : (
    <span
      className="avatar"
      role="img"
      aria-label={avatarId}
      style={{
        ...box,
        // Block-level flex (NOT the `.avatar` class's `inline-flex`): an inline box
        // sits on the text baseline, so on Android the emoji's font descent leaks a
        // sliver below the disc / out of a non-flex ring wrapper. Centring the glyph
        // here (with a line-height BOUND to 1) keeps it inside the exact `size` box.
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.52,
        lineHeight: 1,
        background:
          AVATAR_GRADIENT[avatarId] ?? "linear-gradient(140deg,var(--felt),var(--felt-2))",
      }}
    >
      {AVATAR_EMOJI[avatarId] ?? "🎴"}
    </span>
  );

  if (!ringClass) return stamp(core, size, official);

  // THE DETERMINISTIC RING FRAME. The Tailwind `ringClass` supplies colour/shadow
  // only; every piece of GEOMETRY is pinned inline so no class (or platform) can
  // break it: a BLOCK-level flex box (no baseline strut), border-box sized to
  // exactly avatar + 2×ring on BOTH axes (min=max, shrink-locked — Tailwind
  // preflight is OFF, so box-sizing/border-style must be explicit here),
  // `borderStyle:solid` (preflight normally supplies it), `overflow:hidden` to
  // clip the child to the circle, and `isolation:isolate` to defeat Chromium's
  // border-radius-doesn't-clip-composited-children quirk.
  const outer = size + 2 * ringWidth;
  return stamp(
    <span
      className={ringClass}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: outer,
        height: outer,
        minWidth: outer,
        minHeight: outer,
        maxWidth: outer,
        maxHeight: outer,
        flexShrink: 0,
        boxSizing: "border-box",
        borderWidth: ringWidth,
        borderStyle: "solid",
        borderRadius: 9999,
        overflow: "hidden",
        isolation: "isolate",
      }}
    >
      {core}
    </span>,
    outer,
    official,
  );
}

/**
 * Stamp the verified badge on a finished avatar box WITHOUT changing its geometry.
 *
 * The wrapper is pinned to EXACTLY the box it wraps (min = max = `box`, shrink-locked,
 * border-box) and the badge is `position:absolute` INSIDE it, so it occupies the same layout
 * space the bare avatar did and contributes nothing of its own — an official account can never
 * push a seat, a name or a row by a pixel. That is the property the operator asked to be proven,
 * and it holds by construction rather than by tuning.
 *
 * Two deliberate choices: the wrapper is a BLOCK-level flex (never inline — an inline box sits on
 * the text baseline and Android's Chromium adds the strut, the recurring avatar-overflow bug this
 * file exists to prevent); and the badge sits fully INSIDE the box (`bottom:0`/`right:0`, no
 * negative offsets) so an ancestor with `overflow:hidden` — several table seats have one — cannot
 * clip half a tick off.
 */
function stamp(core: ReactNode, box: number, official?: boolean): ReactNode {
  if (!official) return core;
  // Scaled to the disc, floored at 10px: below that the tick stops reading as a tick.
  const badge = Math.max(10, Math.round(box * 0.36));
  return (
    <span
      // Marked so the layout harness can walk THROUGH it: this wrapper is the one node the badge
      // adds, and a proof that flagged its own insertion as a change would flag every run.
      data-avatar-stamp=""
      style={{
        position: "relative",
        display: "flex",
        width: box,
        height: box,
        minWidth: box,
        minHeight: box,
        maxWidth: box,
        maxHeight: box,
        flexShrink: 0,
        boxSizing: "border-box",
      }}
    >
      {core}
      <OfficialBadge
        size={badge}
        nudge={false}
        className="absolute bottom-0 right-0 ring-1 ring-surface-container-highest/80"
      />
    </span>
  );
}
